/**
 * Actor v3 API型定義
 * プロセス代数とLangChain.js互換性
 * https://actor.gftd.ai/api/v3/actor
 */

// UUID generation - using Web Crypto API

// ===== Core Types =====

export type ActorID = string

export interface Message {
  id: string
  label: string
  payload: Record<string, any>
  timestamp: string
  sender?: ActorID
}

export interface State {
  [key: string]: any
}

export interface OutAction {
  type: 'send' | 'spawn' | 'terminate'
  target?: ActorID
  message?: Message
  behavior?: Behavior
}

export interface BehaviorResult {
  new_state: State
  actions: OutAction[]
  error?: string
}

export interface Behavior {
  id: string
  name: string
  description: string
  version: string
  handler: (message: Message, state: State) => Promise<BehaviorResult>
  metadata: {
    created_at: string
    updated_at: string
    author: string
    tags: string[]
    capabilities: string[]
  }
  process_algebra: {
    notation: 'CCS' | 'π-calculus' | 'ACP'
    expression: string
    channels: string[]
  }
}

// ===== API Request/Response Types =====

export interface CreateActorRequest {
  behavior: Behavior
  organization_id: string
  initial_state?: State
}

export interface CreateActorResponse {
  actor_id: ActorID
  status: 'created'
  timestamp: string
}

export interface SendMessageRequest {
  target: ActorID
  message: Omit<Message, 'id' | 'timestamp'>
}

export interface SendMessageResponse {
  message_id: string
  status: 'sent'
  timestamp: string
}

export interface ReceiveMessagesRequest {
  max_messages?: number
  timeout?: number
  filter?: {
    labels?: string[]
    sender?: ActorID
  }
}

export interface ReceiveMessagesResponse {
  messages: Message[]
  behavior_results: BehaviorResult[]
  timestamp: string
}

export interface ListActorsResponse {
  actors: {
    actor_id: ActorID
    behavior_name: string
    status: 'active' | 'inactive' | 'terminated'
    created_at: string
  }[]
  total_count: number
}

export interface ActorSystemConfig {
  process_algebra_analysis: {
    deadlock_free: boolean
    livelock_free: boolean
    deadlock_actors?: ActorID[]
    livelock_patterns?: string[]
  }
  communication_topology: {
    edges: {
      from: ActorID
      to: ActorID
      channel: string
      weight: number
    }[]
  }
}

export interface SystemStatsResponse {
  stats: {
    active_actors: number
    total_messages: number
    average_latency_ms: number
  }
  detailed_metrics: {
    performance_metrics: {
      throughput: number
      latency_p95: number
    }
  }
}

// ===== LangChain.js Compatibility Types =====

export interface LangChainMessage {
  role: "user" | "assistant" | "system"
  content: string
  name?: string
  function_call?: any
}

export interface RunnableConfig {
  timeout?: number
  maxMessages?: number
}

export interface ActorV3RunnableInterface {
  invoke(input: any, config?: RunnableConfig): Promise<any>
  stream(input: any, config?: RunnableConfig): AsyncGenerator<any, any, unknown>
  batch(inputs: any[], config?: RunnableConfig): Promise<any[]>
  pipe<T>(next: T): T
  withFallbacks(fallbacks: any[]): any
}

// ===== Utility Functions =====

export function createMessage(label: string, payload: Record<string, any>, sender?: ActorID): Message {
  return {
    id: crypto.randomUUID(),
    label,
    payload,
    timestamp: new Date().toISOString(),
    sender
  }
}

export function langChainToActorV3(lcMsg: LangChainMessage): Message {
  return {
    id: crypto.randomUUID(),
    label: lcMsg.name || 'langchain_message',
    payload: { content: lcMsg.content },
    timestamp: new Date().toISOString(),
    sender: lcMsg.role === 'user' ? 'user' : undefined
  }
}

export function actorV3ToLangChain(actorMsg: Message): LangChainMessage {
  return {
    role: actorMsg.sender ? "user" : "assistant",
    content: typeof actorMsg.payload === 'string' ? actorMsg.payload : JSON.stringify(actorMsg.payload),
    name: actorMsg.label
  }
}

// ===== Error Types =====

export interface ActorV3Error {
  code: string
  message: string
  details?: Record<string, any>
}

export interface ApiV3Response<T> {
  success: boolean
  data?: T
  error?: ActorV3Error
} 